type Rule<T> = T | { value: T; message: string };

export interface ValidationRule<TValues extends Record<string, string> = Record<string, string>> {
  required?: boolean | string;
  minLength?: Rule<number>;
  maxLength?: Rule<number>;
  min?: Rule<number>;
  max?: Rule<number>;
  pattern?: Rule<RegExp>;
  email?: boolean | string;
  /** Runs last, after the built-in rules pass; return a message to fail, or nothing to pass. */
  validate?: (value: string, values: TValues) => string | undefined | void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The bare value out of a rule, whether it was given plain or as `{ value, message }`. */
export function ruleValue<T>(rule: Rule<T> | undefined): T | undefined {
  if (rule == null) return undefined;
  return typeof rule === 'object' && rule !== null && 'value' in rule ? (rule as { value: T }).value : (rule as T);
}

function ruleMessage<T>(rule: Rule<T> | undefined, fallback: string): string {
  return typeof rule === 'object' && rule !== null && 'message' in rule ? (rule as { message: string }).message : fallback;
}

/** Runs one field's rules against its current value; returns the first failing message, if any. */
export function validateValue<TValues extends Record<string, string>>(
  value: string,
  rule: ValidationRule<TValues> | undefined,
  values: TValues,
): string | undefined {
  if (!rule) return undefined;
  const trimmed = value.trim();

  if (rule.required && trimmed === '') {
    return typeof rule.required === 'string' ? rule.required : 'This field is required.';
  }
  if (trimmed === '') return undefined;

  const minLength = ruleValue(rule.minLength);
  if (minLength != null && trimmed.length < minLength) return ruleMessage(rule.minLength, `Must be at least ${minLength} characters.`);

  const maxLength = ruleValue(rule.maxLength);
  if (maxLength != null && trimmed.length > maxLength) return ruleMessage(rule.maxLength, `Must be at most ${maxLength} characters.`);

  const min = ruleValue(rule.min);
  if (min != null && Number(trimmed) < min) return ruleMessage(rule.min, `Must be at least ${min}.`);

  const max = ruleValue(rule.max);
  if (max != null && Number(trimmed) > max) return ruleMessage(rule.max, `Must be at most ${max}.`);

  const pattern = ruleValue(rule.pattern);
  if (pattern != null && !pattern.test(trimmed)) return ruleMessage(rule.pattern, 'Invalid format.');

  if (rule.email && !EMAIL_RE.test(trimmed)) return typeof rule.email === 'string' ? rule.email : 'Enter a valid email address.';

  return rule.validate?.(value, values) || undefined;
}

/** Validates every field against its own rule; returns only the fields that failed. */
export function validateFields<TValues extends Record<string, string>>(
  fields: { name: keyof TValues & string; validation?: ValidationRule<TValues> }[],
  values: TValues,
): Partial<Record<keyof TValues, string>> {
  const errors: Partial<Record<keyof TValues, string>> = {};
  for (const field of fields) {
    const message = validateValue(values[field.name] ?? '', field.validation, values);
    if (message) errors[field.name] = message;
  }
  return errors;
}
